import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  getDoc,
} from "firebase/firestore";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { firestoreProd, firestoreDev } from "../../firebase";
import {
  FaSearch,
  FaChevronDown,
  FaChevronRight,
  FaThumbsUp,
  FaThumbsDown,
  FaClock,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Add some CSS for the accordion
const accordionStyles = `
    .accordion-button:focus {
        box-shadow: none;
    }
    .accordion-arrow {
        transition: transform 0.2s ease;
    }
    .accordion-button[aria-expanded="true"] .accordion-arrow {
        transform: rotate(180deg);
    }
    .accordion-button {
        color: #495057 !important;
    }
    .accordion-button:hover {
        background-color: #f8f9fa;
    }
    .task-result-markdown { font-size: 14px; line-height: 1.55; color: #212529; }
    .task-result-markdown > *:first-child { margin-top: 0; }
    .task-result-markdown > *:last-child { margin-bottom: 0; }
    .task-result-markdown h1 { font-size: 1.3rem; }
    .task-result-markdown h2 { font-size: 1.15rem; }
    .task-result-markdown h3 { font-size: 1.05rem; }
    .task-result-markdown table { border-collapse: collapse; width: 100%; margin: 8px 0; }
    .task-result-markdown th,
    .task-result-markdown td { border: 1px solid #dee2e6; padding: 4px 8px; text-align: left; vertical-align: top; }
    .task-result-markdown th { background: #f8f9fa; }
    .task-result-markdown code { background: #f1f3f5; padding: 1px 4px; border-radius: 4px; }
    .task-result-markdown pre { background: #f8f9fa; padding: 10px; border-radius: 6px; overflow: auto; }
    .task-result-markdown img { max-width: 100%; height: auto; }
`;

/**
 * Renders markdown task results the same way the client app does
 * (GitHub-flavored markdown). Scoped styles above keep tables/headings tidy.
 */
const TaskResultMarkdown = ({ markdown }) => (
  <div
    className="task-result-markdown"
    style={{
      background: "#fff",
      border: "1px solid #dee2e6",
      borderRadius: 6,
      padding: "12px 14px",
      maxHeight: 520,
      overflow: "auto",
      wordBreak: "break-word",
    }}
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown || ""}</ReactMarkdown>
  </div>
);

const looksLikeHtml = (value) => /<\/?[a-z][\s\S]*?>/i.test(value || "");

/**
 * Some stored task results are over-escaped (literal `\r`, `\n`, `\t` and `\"`
 * sequences instead of real characters). That breaks both readability and the
 * HTML class attributes (e.g. `class=\"space-y-6\"`), so Tailwind can't match.
 * Normalize those back to real characters before rendering.
 */
const unescapeStored = (value) => {
  // Only the over-escaped HTML results need unescaping — their signature is
  // escaped quotes inside attributes (e.g. class=\"space-y-6\"). Markdown must
  // be left untouched, otherwise LaTeX like \times / \frac gets mangled
  // (\t -> spaces turns "\times" into "imes").
  if (!value || !value.includes('\\"')) {
    return value || "";
  }
  return value
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\\t/g, "  ")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\//g, "/")
    .replace(/\\\\/g, "\\")
    // Drop leftover stray escape backslashes sitting before whitespace / EOL.
    .replace(/\\(\s|$)/g, "$1");
};

/**
 * Renders HTML task results exactly like the client's IframeContent: the HTML
 * is written straight into an iframe document. Tailwind Play CDN is injected so
 * utility classes (bg-white, text-slate-900, grid, …) resolve, matching the
 * borrower/loan-team view. The iframe auto-sizes to its content height.
 */
const TaskResultFrame = ({ html }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    const doc = iframe && iframe.contentDocument;
    if (!doc) return undefined;

    const docHtml =
      "<!DOCTYPE html><html><head><meta charset=\"utf-8\">" +
      '<script src="https://cdn.tailwindcss.com"></scr' +
      "ipt>" +
      "<style>" +
      "body{font-family:'Inter',-apple-system,'Segoe UI',Roboto,sans-serif;margin:14px;color:#0f172a;}" +
      "img{max-width:100%;height:auto;}" +
      "table{max-width:100%;}" +
      "</style></head><body>" +
      (html || "") +
      "</body></html>";

    doc.open();
    doc.write(docHtml);
    doc.close();

    // Cap the height so long results scroll vertically inside the iframe
    // instead of growing the page indefinitely.
    const MAX_HEIGHT = 520;
    const updateHeight = () => {
      try {
        const height = doc.body && doc.body.scrollHeight;
        if (height) {
          iframe.style.height = `${Math.min(height + 24, MAX_HEIGHT)}px`;
        }
      } catch {
        // ignore – keep current height
      }
    };

    // Tailwind CDN injects styles asynchronously, so re-measure a few times.
    const timers = [150, 500, 1200, 2000].map((ms) =>
      setTimeout(updateHeight, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      title="Task result preview"
      style={{
        width: "100%",
        minHeight: 120,
        border: "1px solid #dee2e6",
        borderRadius: 6,
        background: "#fff",
      }}
    />
  );
};

/**
 * Chooses the best rendering for a stored task result. Prefers the captured
 * rendered HTML (taskContent) when present, otherwise renders the stored
 * result as HTML or markdown depending on its format.
 */
const TaskResultView = ({ content, result }) => {
  const cleanedContent = unescapeStored(content);
  const cleanedResult = unescapeStored(result);
  const html = cleanedContent || cleanedResult || "";
  if (looksLikeHtml(html)) {
    return <TaskResultFrame html={html} />;
  }
  return <TaskResultMarkdown markdown={cleanedResult} />;
};

const FeedbackPage = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [expandedFeedbacks, setExpandedFeedbacks] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [env, setEnv] = useState("prod");

  // Select the Firestore instance for the active environment.
  const db = env === "dev" ? firestoreDev : firestoreProd;

  // Compute filtered feedbacks based on search input
  const filteredFeedbacks = feedbacks.filter((feedback) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();
    const userNameMatch = feedback.userName
      ?.toLowerCase()
      .includes(searchLower);
    const userEmailMatch = feedback.userEmail
      ?.toLowerCase()
      .includes(searchLower);
    const taskTitleMatch = feedback.taskTitle
      ?.toLowerCase()
      .includes(searchLower);
    const reasonMatch = feedback.reason?.toLowerCase().includes(searchLower);
    const orgNameMatch = feedback.organizationName
      ?.toLowerCase()
      .includes(searchLower);

    return (
      userNameMatch ||
      userEmailMatch ||
      taskTitleMatch ||
      reasonMatch ||
      orgNameMatch
    );
  });

  const fetchOrganizations = useCallback(async (organizationIds, activeDb) => {
    const organizationsMap = {};

    if (!organizationIds || organizationIds.length === 0) {
      return organizationsMap;
    }

    try {
      // Fetch each organization by ID
      const fetchPromises = organizationIds.map(async (orgId) => {
        try {
          const orgRef = doc(activeDb, "organizations", orgId);
          const orgSnapshot = await getDoc(orgRef);

          if (orgSnapshot.exists()) {
            const orgData = orgSnapshot.data();
            return { id: orgId, name: orgData.name || orgId };
          }
          return { id: orgId, name: null };
        } catch (error) {
          console.warn(`Error fetching organization ${orgId}:`, error);
          return { id: orgId, name: null };
        }
      });

      const orgResults = await Promise.all(fetchPromises);

      // Build map of organizationId -> organizationName
      orgResults.forEach(({ id, name }) => {
        organizationsMap[id] = name;
      });

      console.log("Organizations map:", organizationsMap);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }

    return organizationsMap;
  }, []);

  const fetchFeedbacks = useCallback(async () => {
    if (!db) {
      toast.error("Firebase is not initialized");
      return;
    }

    setIsLoading(true);
    setError(null);
    setFeedbacks([]);
    setHasLoaded(true);

    try {
      const feedbacksRef = collection(db, "task-feedbacks");
      const q = query(feedbacksRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        toast.info("No feedbacks found");
        setFeedbacks([]);
        return;
      }

      const feedbacksList = [];
      const organizationIds = new Set();

      for (const docSnapshot of querySnapshot.docs) {
        const feedbackData = docSnapshot.data();

        // Debug: Log first feedback to see data structure
        if (feedbacksList.length === 0) {
          console.log("First feedback data:", feedbackData);
        }

        feedbacksList.push({
          id: docSnapshot.id,
          ...feedbackData,
        });

        // Collect unique organization IDs
        if (feedbackData.organizationId) {
          organizationIds.add(feedbackData.organizationId);
        }
      }

      // Fetch all organizations
      console.log("Fetching organizations:", Array.from(organizationIds));
      const organizationsMap = await fetchOrganizations(
        Array.from(organizationIds),
        db
      );

      // Add organization names to feedbacks
      const feedbacksWithOrgNames = feedbacksList.map((feedback) => ({
        ...feedback,
        organizationName: feedback.organizationId
          ? organizationsMap[feedback.organizationId] || null
          : null,
      }));

      setFeedbacks(feedbacksWithOrgNames);
      toast.success(`Loaded ${feedbacksWithOrgNames.length} feedbacks`);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
      setError("Failed to fetch feedbacks");
      toast.error("Failed to fetch feedbacks");
    } finally {
      setIsLoading(false);
    }
  }, [fetchOrganizations, db]);

  // Load all feedbacks on component mount
  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  const toggleFeedbackExpansion = (feedbackId) => {
    setExpandedFeedbacks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(feedbackId)) {
        newSet.delete(feedbackId);
      } else {
        newSet.add(feedbackId);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const renderSentimentBadge = (positive) => {
    if (positive) {
      return (
        <span className="badge bg-success">
          <FaThumbsUp className="me-1" />
          Positive
        </span>
      );
    } else {
      return (
        <span className="badge bg-danger">
          <FaThumbsDown className="me-1" />
          Negative
        </span>
      );
    }
  };

  return (
    <div className="container-fluid p-4 FeedbackPageContainer">
      <style dangerouslySetInnerHTML={{ __html: accordionStyles }} />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <h1>
        <FaThumbsUp className="me-2" />
        Task Feedbacks
      </h1>

      {/* Instructions Section - Collapsible */}
      <div className="card mb-4">
        <div className="card-header">
          <h2 className="h5 mb-0">
            <button
              className="accordion-button collapsed p-2 text-decoration-none w-100 d-flex justify-content-between align-items-center border-0"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#instructionsCollapse"
              aria-expanded="false"
              aria-controls="instructionsCollapse"
            >
              <span>📖 How to View Task Feedbacks</span>
              <span className="accordion-arrow">▼</span>
            </button>
          </h2>
        </div>
        <div className="collapse" id="instructionsCollapse">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <h6>✅ How to Use:</h6>
                <ol>
                  <li>
                    All feedbacks are automatically loaded when you open this
                    page
                  </li>
                  <li>Feedbacks are sorted by creation date (newest first)</li>
                  <li>
                    Use the search box to filter by user name, email, task
                    title, or feedback reason
                  </li>
                  <li>Click on any feedback row to see full details</li>
                  <li>
                    Green badge indicates positive feedback (👍), red indicates
                    negative feedback (👎)
                  </li>
                </ol>
              </div>
              <div className="col-md-6">
                <h6>🔍 Search Options:</h6>
                <ul>
                  <li>User name or email</li>
                  <li>Organization name</li>
                  <li>Task title</li>
                  <li>Feedback reason</li>
                </ul>
                <h6 className="mt-3">📊 Feedback Information:</h6>
                <ul>
                  <li>
                    <strong>User:</strong> Name and email of person who
                    submitted feedback
                  </li>
                  <li>
                    <strong>Task:</strong> Title of the task being reviewed
                  </li>
                  <li>
                    <strong>Organization:</strong> Organization name associated
                    with the loan
                  </li>
                  <li>
                    <strong>Sentiment:</strong> Positive (👍) or Negative (👎)
                  </li>
                  <li>
                    <strong>Created:</strong> When the feedback was submitted
                  </li>
                </ul>
              </div>
            </div>

            <div className="alert alert-info mt-3" role="alert">
              <strong>💡 Tip:</strong> Click on any feedback row to expand and
              see complete details including task results, status, and user
              feedback reason.
            </div>
          </div>
        </div>
      </div>

      {/* Feedbacks List */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2 className="h5 mb-0">Task Feedbacks</h2>
              <small className="text-muted">
                View user feedback on AI tasks
              </small>
            </div>
            <div
              className="btn-group btn-group-sm"
              role="group"
              aria-label="Environment"
            >
              <button
                type="button"
                className={`btn ${
                  env === "prod" ? "btn-success" : "btn-outline-success"
                }`}
                onClick={() => setEnv("prod")}
                disabled={isLoading}
              >
                Prod
              </button>
              <button
                type="button"
                className={`btn ${
                  env === "dev" ? "btn-warning" : "btn-outline-warning"
                }`}
                onClick={() => setEnv("dev")}
                disabled={isLoading}
              >
                Dev
              </button>
            </div>
          </div>
          {isLoading && (
            <div className="mt-2">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></div>
              <span className="text-muted">Loading feedbacks...</span>
            </div>
          )}
          {feedbacks.length > 0 && (
            <div className="mt-2">
              <span className="text-muted">
                Total feedbacks: {feedbacks.length}
                {searchTerm.trim() && (
                  <span> | Filtered: {filteredFeedbacks.length}</span>
                )}
              </span>
            </div>
          )}
        </div>
        <div className="card-body">
          {/* Real-time search input for table */}
          {feedbacks.length > 0 && (
            <div className="mb-3">
              <div className="row">
                <div className="col-md-6">
                  <label htmlFor="feedbackSearch" className="form-label">
                    Filter Feedbacks
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      id="feedbackSearch"
                      placeholder="Filter by user, organization, task, or reason"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setSearchTerm("")}
                        title="Clear filter"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {searchTerm && (
                    <small className="text-muted">
                      Showing {filteredFeedbacks.length} of {feedbacks.length}{" "}
                      feedbacks
                    </small>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Feedbacks Table with Accordion */}
          {filteredFeedbacks.length > 0 && (
            <div>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th width="50"></th>
                      <th>User</th>
                      <th>Task</th>
                      <th>Organization</th>
                      <th>Sentiment</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFeedbacks.map((feedback) => {
                      const isExpanded = expandedFeedbacks.has(feedback.id);

                      return (
                        <React.Fragment key={feedback.id}>
                          {/* Main Row - Clickable */}
                          <tr
                            className="cursor-pointer"
                            onClick={() => toggleFeedbackExpansion(feedback.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              {isExpanded ? (
                                <FaChevronDown className="text-muted" />
                              ) : (
                                <FaChevronRight className="text-muted" />
                              )}
                            </td>
                            <td>
                              {feedback.userName ? (
                                <div>
                                  <div>{feedback.userName}</div>
                                  <small className="text-muted">
                                    {feedback.userEmail || "No email"}
                                  </small>
                                </div>
                              ) : feedback.userEmail ? (
                                <small>{feedback.userEmail}</small>
                              ) : (
                                <span className="text-muted">Unknown User</span>
                              )}
                            </td>
                            <td>
                              {feedback.taskTitle || (
                                <span className="text-muted">No title</span>
                              )}
                            </td>
                            <td>
                              {feedback.organizationName ? (
                                <div>
                                  <div>{feedback.organizationName}</div>
                                  <small className="text-muted font-monospace">
                                    {feedback.organizationId}
                                  </small>
                                </div>
                              ) : feedback.organizationId ? (
                                <small className="font-monospace">
                                  {feedback.organizationId}
                                </small>
                              ) : (
                                <span className="text-muted">N/A</span>
                              )}
                            </td>
                            <td>{renderSentimentBadge(feedback.positive)}</td>
                            <td>
                              <FaClock className="me-1 text-muted" />
                              {formatTimestamp(feedback.createdAt)}
                            </td>
                          </tr>

                          {/* Expanded Row - Full Details */}
                          {isExpanded && (
                            <tr>
                              <td colSpan="6" className="bg-light">
                                <div className="p-3">
                                  <h6 className="mb-3">
                                    <FaThumbsUp className="me-2" />
                                    Feedback Details
                                  </h6>

                                  <div className="row">
                                    <div className="col-md-6">
                                      <h6 className="text-muted mb-2">
                                        Task Information
                                      </h6>
                                      <table className="table table-sm table-borderless">
                                        <tbody>
                                          <tr>
                                            <td>
                                              <strong>Task Title:</strong>
                                            </td>
                                            <td>
                                              {feedback.taskTitle || "N/A"}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong>Task ID:</strong>
                                            </td>
                                            <td>
                                              <small className="font-monospace">
                                                {feedback.taskId || "N/A"}
                                              </small>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong>Task Status:</strong>
                                            </td>
                                            <td>
                                              {feedback.taskStatus ? (
                                                <span className="badge bg-info">
                                                  {feedback.taskStatus}
                                                </span>
                                              ) : (
                                                "N/A"
                                              )}
                                            </td>
                                          </tr>
                                          {feedback.taskStatusReason && (
                                            <tr>
                                              <td>
                                                <strong>Status Reason:</strong>
                                              </td>
                                              <td>
                                                {feedback.taskStatusReason}
                                              </td>
                                            </tr>
                                          )}
                                          {feedback.taskResult && (
                                            <tr>
                                              <td>
                                                <strong>Task Result:</strong>
                                              </td>
                                              <td>
                                                <TaskResultView
                                                  content={feedback.taskContent}
                                                  result={feedback.taskResult}
                                                />
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>

                                    <div className="col-md-6">
                                      <h6 className="text-muted mb-2">
                                        User Feedback
                                      </h6>
                                      <table className="table table-sm table-borderless">
                                        <tbody>
                                          <tr>
                                            <td>
                                              <strong>User:</strong>
                                            </td>
                                            <td>
                                              {feedback.userName || "Unknown"}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong>Email:</strong>
                                            </td>
                                            <td>
                                              {feedback.userEmail ||
                                                "No email"}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong>User ID:</strong>
                                            </td>
                                            <td>
                                              <small className="font-monospace">
                                                {feedback.userId || "N/A"}
                                              </small>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong>Sentiment:</strong>
                                            </td>
                                            <td>
                                              {renderSentimentBadge(
                                                feedback.positive
                                              )}
                                            </td>
                                          </tr>
                                          {feedback.reason && (
                                            <tr>
                                              <td>
                                                <strong>Reason:</strong>
                                              </td>
                                              <td>
                                                <div
                                                  className="alert alert-warning mb-0 p-2"
                                                  style={{
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                  }}
                                                >
                                                  {feedback.reason}
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>

                                  <div className="row mt-3">
                                    <div className="col-md-12">
                                      <h6 className="text-muted mb-2">
                                        Additional Information
                                      </h6>
                                      <table className="table table-sm table-borderless">
                                        <tbody>
                                          <tr>
                                            <td>
                                              <strong>Loan ID:</strong>
                                            </td>
                                            <td>
                                              {feedback.loanId ? (
                                                <a
                                                  href={`https://app.addy.so/loans?tab=board&loan=${feedback.loanId}&person=example%40example.com&tabValue=processingChecklist`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-decoration-none"
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  <small className="font-monospace text-primary">
                                                    {feedback.loanId}
                                                  </small>
                                                </a>
                                              ) : (
                                                "N/A"
                                              )}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong>Organization:</strong>
                                            </td>
                                            <td>
                                              {feedback.organizationName
                                                ? `${feedback.organizationName} (${feedback.organizationId})`
                                                : feedback.organizationId ||
                                                  "N/A"}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong>Created At:</strong>
                                            </td>
                                            <td>
                                              {formatTimestamp(
                                                feedback.createdAt
                                              )}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong>Updated At:</strong>
                                            </td>
                                            <td>
                                              {formatTimestamp(
                                                feedback.updatedAt
                                              )}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {filteredFeedbacks.length === 0 &&
            feedbacks.length > 0 &&
            searchTerm.trim() && (
              <div className="alert alert-info" role="alert">
                <FaSearch className="me-2" />
                No feedbacks found matching "{searchTerm}". Try a different
                search term.
              </div>
            )}

          {feedbacks.length === 0 && hasLoaded && !isLoading && (
            <div className="alert alert-info" role="alert">
              No feedbacks found.
            </div>
          )}

          {!hasLoaded && !isLoading && (
            <div className="alert alert-info" role="alert">
              Loading feedbacks...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
